import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Zap } from 'lucide-react';
import { type StackItem } from '../types';
import { StackInspector } from './StackInspector';

interface CompetencyMatrixProps {
  stacks: StackItem[];
  selectedStackId: string;
  onSelectStackId: (id: string) => void;
}

export const CompetencyMatrix: React.FC<CompetencyMatrixProps> = ({
  stacks,
  selectedStackId,
  onSelectStackId,
}) => {
  const { t } = useTranslation();
  const [activeCategoryFilter, setActiveCategoryFilter] = useState('ALL');

  // Extract unique category keys or categories
  const categoryKeys = ['ALL', ...Array.from(new Set(stacks.map(s => s.categoryKey)))];

  const filteredStacks = activeCategoryFilter === 'ALL'
    ? stacks
    : stacks.filter(s => s.categoryKey === activeCategoryFilter);

  const selectedStack = stacks.find(s => s.id === selectedStackId) || stacks[0];

  return (
    <section className="py-12 border-b border-gray-800/80">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <span className="text-xs font-mono text-[#00F0FF] tracking-widest">{t('matrix.badge')}</span>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mt-1">{t('matrix.title')}</h2>
        </div>

        {/* Filter buttons */}
        <div className="flex flex-wrap gap-2 font-mono text-xs">
          {categoryKeys.map((catKey) => {
            const label = catKey === 'ALL'
              ? t('matrix.categoryAll')
              : (t(`matrix.categories.${catKey}`, { defaultValue: catKey }));

            const isActive = activeCategoryFilter === catKey;

            return (
              <button
                key={catKey}
                type="button"
                onClick={() => setActiveCategoryFilter(catKey)}
                className={`px-3 py-1.5 transition-all cursor-pointer ${
                  isActive
                    ? 'bg-[#00F0FF] text-[#07090E] font-bold'
                    : 'bg-[#0E111B] text-gray-400 hover:text-white border border-gray-800'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

        {/* Left: Stack Grid */}
        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {filteredStacks.map((item) => {
            const isSelected = selectedStackId === item.id;
            return (
              <div
                key={item.id}
                onClick={() => onSelectStackId(item.id)}
                className={`p-4 cursor-pointer transition-all border font-mono text-xs relative ${
                  isSelected
                    ? 'border-[#00F0FF] bg-[#00F0FF]/5 text-white shadow-[0_0_15px_rgba(0,240,255,0.1)]'
                    : 'border-gray-800/80 bg-[#090C13] text-gray-400 hover:border-gray-600 hover:text-gray-200'
                }`}
              >
                <div className="flex justify-between items-start">
                  <span className="text-gray-500 text-[10px]">{item.category}</span>
                  <span className={`text-[10px] ${isSelected ? 'text-[#00F0FF]' : 'text-gray-600'}`}>
                    {item.experience}
                  </span>
                </div>

                <div className="text-base font-bold text-white mt-2 flex items-center justify-between">
                  <span>{item.name}</span>
                  {isSelected && <Zap size={14} className="text-[#00F0FF]" />}
                </div>

                <div className="mt-2 text-[11px] text-gray-400 line-clamp-1 font-sans">
                  {item.highlight}
                </div>
              </div>
            );
          })}
        </div>

        {/* Right: Selected Stack Detail Panel */}
        <div className="lg:col-span-5">
          <StackInspector stack={selectedStack} />
        </div>

      </div>
    </section>
  );
};
